// Generated by TaroBase SDK Generator: 2026-08-23T03:59:35.511Z
// Collection: adminSettings/$settingId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Stores admin-only configuration values such as API keys (e.g. OpenAI API key). Each document is keyed by a known settingId like 'openaiKey'. Only the admin (ADMIN_ADDRESS) can read or write these values, ensuring secrets remain private to the project owner.
 */

export interface AdminSettingsRequest {
  value: string;
  updatedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface AdminSettingsResponse {
  value: string;
  updatedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a AdminSettings operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildAdminSettings(settingId: string, data: AdminSettingsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `adminSettings/${settingId}`, document: doc };
}

/**
 * Admin only. Creates a new admin setting document under a known settingId (e.g. 'openaiKey'). Frontend must pass `value` (string) and `updatedAt` (Unix seconds UInt). No hooks; pure offchain storage. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setAdminSettings(settingId: string, data: AdminSettingsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`adminSettings/${settingId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting AdminSettings: ${formatError(error)}`);
    return false;
  }
}

export type AdminSettingsRequestUpdate = Partial<AdminSettingsRequest>;

/**
 * Build a AdminSettings update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateAdminSettings(settingId: string, data: AdminSettingsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `adminSettings/${settingId}`, document: doc };
}

/**
 * Admin only. Updates the `value` and `updatedAt` fields for an existing setting. Frontend should refresh `updatedAt` to current Unix seconds whenever changing `value`. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateAdminSettings(settingId: string, data: AdminSettingsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`adminSettings/${settingId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating AdminSettings: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Admin only. Returns the stored setting value (e.g. an API key). Anyone other than ADMIN_ADDRESS is denied.
   (Get Single Item)
 */
export async function getAdminSettings(settingId: string): Promise<AdminSettingsResponse | null> {
  try {
    const result = await get(`adminSettings/${settingId}`);
    return result as AdminSettingsResponse | null;
  } catch (error) {
    console.error(`Error getting AdminSettings: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single AdminSettings document. (
  Read Operation Details: Admin only. Returns the stored setting value (e.g. an API key). Anyone other than ADMIN_ADDRESS is denied.
  )
 */
export function subscribeAdminSettings(callback: (data: AdminSettingsResponse | null) => void, settingId: string): Promise<() => Promise<void>> {
  return subscribe(`adminSettings/${settingId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as AdminSettingsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to AdminSettings at path '${`adminSettings/${settingId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many AdminSettings items from collection adminSettings
 
  Read Operation Details: Admin only. Returns the stored setting value (e.g. an API key). Anyone other than ADMIN_ADDRESS is denied.
  
 */
export async function getManyAdminSettings(filter: string = ""): Promise<AdminSettingsResponse[]> {
  try {
    const results = await get("adminSettings", { prompt: filter });
    return Array.isArray(results) ? results as AdminSettingsResponse[] : [];
  } catch (error) {
    console.error(`Error getting AdminSettings collection at path 'adminSettings': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in AdminSettings collection at adminSettings
 
  Read Operation Details: Admin only. Returns the stored setting value (e.g. an API key). Anyone other than ADMIN_ADDRESS is denied.
  
 */
export function subscribeManyAdminSettings(callback: (data: AdminSettingsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("adminSettings", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as AdminSettingsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to AdminSettings collection at path '${"adminSettings"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all AdminSettings items from collection adminSettings
 
  Read Operation Details: Admin only. Returns the stored setting value (e.g. an API key). Anyone other than ADMIN_ADDRESS is denied.
  
 */
export async function getAllAdminSettings(filter: string = ""): Promise<AdminSettingsResponse[]> {
  try {
    const results = await get("adminSettings", { prompt: filter });
    return Array.isArray(results) ? results as AdminSettingsResponse[] : [];
  } catch (error) {
    console.error(`Error getting AdminSettings collection at path 'adminSettings': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in AdminSettings collection at adminSettings
 
  Read Operation Details: Admin only. Returns the stored setting value (e.g. an API key). Anyone other than ADMIN_ADDRESS is denied.
  
 */
export function subscribeAllAdminSettings(callback: (data: AdminSettingsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("adminSettings", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as AdminSettingsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to AdminSettings collection at path '${"adminSettings"}': ${formatError(error)}`);
    }
  });
}

/**
 * 
  Delete Operation Details: Admin only. Removes an admin setting document. Use to clear a stored secret like an API key.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteAdminSettings(settingId: string): Promise<boolean> {
  try {
    const succeeded = await set(`adminSettings/${settingId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting AdminSettings: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for AdminSettings for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteAdminSettings(settingId: string): DocumentOperation {
  return { path: `adminSettings/${settingId}`, document: null };
}

